import { useState } from 'react';

const faqs = [
  {
    question: 'Is Basetao Spreadsheet free?',
    answer: 'Access to the Basetao Spreadsheet depends on the provider. Some versions are freely shared within community groups, while premium curated versions with regular updates and additional features may require a purchase. The spreadsheet itself serves as a product discovery tool, and using it alongside a Basetao account for actual purchasing is free of charge beyond standard agent service fees.',
  },
  {
    question: 'Is Basetao legit?',
    answer: 'Yes, Basetao is a legitimate and well-established shopping agent platform that has been serving international customers for many years. The platform acts as an intermediary between overseas buyers and Chinese marketplaces, providing services such as product purchasing, quality inspection, warehousing, and international shipping. Thousands of satisfied users worldwide can attest to its reliability.',
  },
  {
    question: 'How much is Basetao shipping?',
    answer: 'Basetao shipping costs vary based on package weight, dimensions, shipping method, and destination country. Express shipping (DHL, FedEx) typically costs more but delivers within 3-7 business days. Air shipping options like EMS and EUB offer a middle ground in terms of cost and speed. Economy sea shipping is the most affordable option for large, non-urgent shipments, with delivery times ranging from 20 to 60 days.',
  },
  {
    question: 'Does Basetao offer coupons?',
    answer: 'Basetao occasionally provides promotional discounts and coupon codes, particularly for new users and during seasonal sales events. Additionally, the platform may offer reduced service fees or shipping discounts during special promotional periods. It is recommended to check the official Basetao website or authorized community channels for the most current coupon and discount code information.',
  },
  {
    question: 'How long does Basetao warehouse store items?',
    answer: 'Basetao typically provides free warehouse storage for a set period, commonly around 90 to 180 days depending on the specific policy at the time. After the free storage period expires, extended storage fees may apply. The warehouse also offers quality control inspection and package consolidation services, allowing you to combine multiple items into a single shipment for cost savings.',
  },
  {
    question: 'How do I use Basetao Spreadsheet?',
    answer: 'Using the Basetao Spreadsheet is straightforward. First, obtain access to the spreadsheet file through the provider. Open it in your preferred spreadsheet application such as Google Sheets or Microsoft Excel. Browse the organized categories to find products of interest. Each entry includes product links that you can copy and paste into the Basetao platform to place orders. The spreadsheet typically includes filters and sorting options to help narrow down selections by price, category, or popularity.',
  },
  {
    question: 'What categories are available in the Basetao Spreadsheet?',
    answer: 'The Basetao Spreadsheet covers a comprehensive range of product categories including Shoes, Hoodies and Sweaters, T-Shirts, Jackets, Pants and Shorts, Headwear, Matching Sets, Underwear and Underpants, Sports Jerseys, Accessories, Bags, Belts, Perfumes, and various other miscellaneous items. Each category is populated with curated product listings complete with pricing and link information.',
  },
  {
    question: 'How often is the Basetao Spreadsheet updated?',
    answer: 'Update frequency depends on the specific spreadsheet version and provider. Premium versions typically receive regular updates ranging from weekly to monthly, ensuring product listings, prices, and availability information remain current. Community-shared versions may be updated less frequently. Always verify critical product details on the Basetao platform before placing orders.',
  },
  {
    question: 'Can I use Basetao Spreadsheet on mobile?',
    answer: 'Absolutely. Since the spreadsheet is typically delivered in standard formats like Google Sheets or Excel, it is fully accessible on mobile devices through the respective apps. The mobile experience allows you to browse products, copy links, and research items on the go, though the desktop version provides a more comfortable browsing experience for extensive product research.',
  },
  {
    question: 'What is the Basetao warehouse QC process?',
    answer: 'When your ordered items arrive at the Basetao warehouse, staff perform a quality control inspection. This typically includes verifying the received product matches the order, checking for obvious defects or damage, confirming the correct size and color, and taking inspection photos. If issues are found, the warehouse team can assist with returns or exchanges before the item is shipped internationally.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="bg-background-100 section-padding">
      <div className="section-container">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide uppercase mb-4">
            FAQ
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-balance">
            Find answers to the most common questions about Basetao Spreadsheet, shipping, warehousing, and shopping tips.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
              >
                <span className="font-medium text-foreground-900 pr-4">{faq.question}</span>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}